import { Link } from "react-router-dom";
import { MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLine } from "@/components/Reveal";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import { fmtPhone, mapsUrl, waLink } from "@/lib/api";

export function WaButton({ testid, className = "", label }) {
  const { t } = useLang();
  const { settings } = useStore();
  if (!settings?.whatsapp_number) return null;
  return (
    <a
      data-testid={testid}
      href={waLink(settings.whatsapp_number, t("wa_contact_msg"))}
      target="_blank"
      rel="noreferrer"
      className={`btn-gold inline-flex items-center justify-center gap-3 border border-[#C6A76A] px-7 py-4 text-[0.75rem] tracking-[0.22em] text-[#F7F3EB] transition-colors duration-500 hover:text-[#0B0B0C] rtl:text-sm rtl:tracking-normal ${className}`}
    >
      <MessageCircle className="h-4 w-4" strokeWidth={1.3} /> {label || t("contact_wa")}
    </a>
  );
}

export function ContactDetails({ large = false }) {
  const { t, tr } = useLang();
  const { settings } = useStore();
  const address = tr(settings?.address);
  const num = settings?.whatsapp_number;
  return (
    <dl className="divide-y divide-[#F7F3EB]/10 border-y border-[#F7F3EB]/10">
      {address && (
        <div className="grid grid-cols-[2.5rem_1fr] gap-4 py-7" data-testid="contact-address">
          <MapPin className="mt-1 h-5 w-5 text-[#C6A76A]" strokeWidth={1.2} />
          <div>
            <dt className="eyebrow">{t("address_label")}</dt>
            <dd className={`font-display mt-3 text-[#F7F3EB] ${large ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}>{address}</dd>
            <a href={mapsUrl(settings?.address?.fr || address)} target="_blank" rel="noreferrer" data-testid="contact-open-maps" className="link-line mt-3 inline-flex items-center gap-1 pb-0.5 text-xs tracking-[0.18em] text-[#F7F3EB]/70 hover:text-[#F7F3EB] rtl:text-sm rtl:tracking-normal">
              {t("open_maps")} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      )}
      {num && (
        <div className="grid grid-cols-[2.5rem_1fr] gap-4 py-7" data-testid="contact-whatsapp">
          <MessageCircle className="mt-1 h-5 w-5 text-[#C6A76A]" strokeWidth={1.2} />
          <div>
            <dt className="eyebrow">{t("whatsapp_label")}</dt>
            <dd className={`mt-3 ${large ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}>
              <a href={waLink(num, t("wa_contact_msg"))} target="_blank" rel="noreferrer" dir="ltr" data-testid="contact-whatsapp-number" className="font-display text-[#F7F3EB] transition-colors hover:text-[#C6A76A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {fmtPhone(num)}
              </a>
            </dd>
          </div>
        </div>
      )}
    </dl>
  );
}

export function ContactSection() {
  const { t, rtl } = useLang();
  const { settings } = useStore();
  if (!settings?.whatsapp_number && !settings?.address) return null;
  return (
    <section id="contact" data-testid="contact-section" className="grain relative overflow-hidden bg-[#241914] text-[#F7F3EB]">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-5 py-28 sm:px-10 lg:grid-cols-12 lg:gap-10 lg:py-36">
        <div className="lg:col-span-6">
          <FadeUp className="flex items-center gap-4">
            <span className="font-latin text-xs tracking-[0.3em] text-[#F7F3EB]/60">04</span>
            <span className="h-px w-10 bg-[#C6A76A]" />
            <span className="eyebrow">{t("contact_over")}</span>
          </FadeUp>
          <h2 className={`font-display mt-8 ${rtl ? "text-4xl leading-[1.6] sm:text-5xl lg:text-6xl" : "text-4xl leading-[1.05] sm:text-5xl lg:text-[4.2rem]"}`}>
            <MaskLine inView>{t("contact_title")}</MaskLine>
          </h2>
          <FadeUp delay={0.15}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#F7F3EB]/75">{t("contact_sub")}</p>
          </FadeUp>
          <FadeUp delay={0.25} className="mt-10 flex flex-wrap items-center gap-6">
            <WaButton testid="contact-section-whatsapp-btn" />
            <Link to="/contact" data-testid="contact-section-page-link" className="link-line pb-1 text-[0.75rem] tracking-[0.22em] text-[#F7F3EB]/80 hover:text-[#F7F3EB] rtl:text-sm rtl:tracking-normal">
              {t("contact")}
            </Link>
          </FadeUp>
        </div>
        <FadeUp delay={0.1} className="lg:col-span-5 lg:col-start-8 lg:self-end">
          <ContactDetails />
        </FadeUp>
      </div>
    </section>
  );
}
